import { useEffect, useRef } from 'react';

import type { MensagemChamadoResponse } from '@/api/generated/model';
import { Avatar } from '@/shared/components/Avatar';
import { cn } from '@/shared/lib/cn';
import { formatarDataHora, ROTULO_EVENTO } from '@/features/suporte/rotulos';

/**
 * A conversa do chamado.
 *
 * COMPONENTE BURRO de propósito: recebe as mensagens por prop em vez de chamar
 * o hook. Os dois lados (solicitante e atendente) consomem endpoints
 * diferentes — `useChamadosControllerListarMensagens` e
 * `useChamadosAdminControllerListarMensagens` — então a reutilização só é
 * possível por props. Um hook compartilhado teria que receber "de que lado
 * estou" e escolher, o que é a mesma decisão espalhada em dois lugares.
 */
export function ThreadDeMensagens({
  mensagens,
  meuProfileId,
  vazio,
  aoAbrirAnexo,
}: {
  mensagens: readonly MensagemChamadoResponse[];
  meuProfileId: string | undefined;
  /** O que mostrar antes da primeira mensagem. */
  vazio?: React.ReactNode;
  aoAbrirAnexo?: (mensagem: MensagemChamadoResponse) => void;
}) {
  const fimDaLista = useRef<HTMLDivElement>(null);

  // Rola para o fim a cada mensagem nova — inclusive as que chegam pelo
  // polling, que é o caso que mais importa: sem isto, a resposta do atendente
  // aparece fora da tela e a pessoa acha que ninguém respondeu.
  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens.length]);

  if (mensagens.length === 0 && vazio) return <>{vazio}</>;

  return (
    <div className="space-y-4">
      {mensagens.map((mensagem) =>
        mensagem.tipo === 'SISTEMA' ? (
          <LinhaDeSistema key={mensagem.id} mensagem={mensagem} />
        ) : (
          <Balao
            key={mensagem.id}
            mensagem={mensagem}
            meu={mensagem.autorId === meuProfileId}
            aoAbrirAnexo={aoAbrirAnexo}
          />
        ),
      )}
      <div ref={fimDaLista} />
    </div>
  );
}

/**
 * Evento no meio da conversa ("assumiu", "previsão definida").
 *
 * Centralizado e sem avatar: não é fala de ninguém, é o chamado mudando de
 * estado. O `evento` decide o rótulo — o `corpo` nunca é parseado.
 */
function LinhaDeSistema({ mensagem }: { mensagem: MensagemChamadoResponse }) {
  const rotulo = mensagem.evento ? ROTULO_EVENTO[mensagem.evento] : null;

  return (
    <div className="flex justify-center">
      <div className="max-w-md rounded-full bg-muted/60 px-4 py-1.5 text-center">
        {rotulo && (
          <span className="mr-1.5 text-xs font-semibold uppercase tracking-wide text-brand">
            {rotulo}
          </span>
        )}
        <span className="text-xs text-muted-foreground">{mensagem.corpo}</span>
      </div>
    </div>
  );
}

function Balao({
  mensagem,
  meu,
  aoAbrirAnexo,
}: {
  mensagem: MensagemChamadoResponse;
  meu: boolean;
  aoAbrirAnexo?: (mensagem: MensagemChamadoResponse) => void;
}) {
  return (
    <div className={cn('flex gap-2', meu ? 'flex-row-reverse' : 'flex-row')}>
      <Avatar fotoUrl={mensagem.autorFotoUrl} nome={mensagem.autorNome} />

      <div className={cn('max-w-[75%] space-y-1', meu && 'items-end text-right')}>
        <p className="text-xs text-muted-foreground">
          {mensagem.autorNome} · {formatarDataHora(mensagem.criadoEm)}
        </p>

        <div
          className={cn(
            'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words text-left',
            meu ? 'bg-brand text-brand-foreground' : 'bg-surface-raised border border-brand/15',
          )}
        >
          {mensagem.corpo}
        </div>

        {mensagem.arquivoId && aoAbrirAnexo && (
          <button
            type="button"
            onClick={() => aoAbrirAnexo(mensagem)}
            className="text-xs text-brand underline underline-offset-2"
          >
            Ver anexo
          </button>
        )}
      </div>
    </div>
  );
}
